import {
  type BiddingApprovalMode,
  BiddingApprovalState,
  BiddingBidSendStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import { isDirectSubmitMode } from './approvalModeResolve'
import { deriveJepxReach } from './bidStatusCategory'

/**
 * 承認済の受渡日の主ボタンが示す送信の進み具合 (design `spot.html` の承認済:
 * 送信軸が JEPX 送信済 → 「JEPX 送信済」、 それ以外 → 「JEPX 送信未完了」)。
 */
export type ApprovedSendKind = 'unsent' | 'sent'

/** 承認済の主ボタンの押せない理由の文末 (request = 「承認申請できません」、 submit = 「提出できません」)。 */
export type ApprovedActionVerb = 'request' | 'submit'

type ApprovedSendKindInput = {
  approvalState: BiddingApprovalState
  approvedBy?: string
  sendStatus: BiddingBidSendStatus
}

/**
 * 承認済として数える依頼を選ぶ。 切替前に作られた承認済 (承認軸 APPROVED) と、 最終承認で提出まで
 * 進んだ依頼 (承認軸 SUBMITTED + 承認者あり)。 承認を経たかは承認者 (approvedBy。 承認でだけ入る)
 * で見る。 承認を経ない提出 (承認なし・自動) は design の承認済ではないので対象にしない。
 * 取り下げられた送信 (CANCELED) も対象にしない。
 */
export const selectApprovedRequests = <T extends ApprovedSendKindInput>(
  requests: readonly T[],
): T[] =>
  requests.filter(
    (request) =>
      request.approvalState === BiddingApprovalState.APPROVED ||
      (request.approvalState === BiddingApprovalState.SUBMITTED &&
        request.approvedBy !== undefined &&
        request.sendStatus !== BiddingBidSendStatus.CANCELED),
  )

/**
 * 受渡日の依頼から、 承認済の主ボタンが示す送信の進み具合を返す。 承認済の依頼が無ければ undefined。
 *
 * design は受渡日あたり 1 つの「承認済」状態を持ち、 承認から JEPX 送信までを送信軸で分ける。
 * 実装では最終承認が同じ呼び出しで提出まで進む (AC-BSP-094) ため、 承認を経た依頼は承認軸
 * SUBMITTED + 送信軸で表れる (承認軸 APPROVED が残るのは切替前の札だけ)。
 *
 * 複数の依頼が並存する時は、 送信が済んでいない依頼が 1 件でもあれば「未完了」。
 */
export const resolveApprovedSendKind = (
  requests: readonly ApprovedSendKindInput[],
): ApprovedSendKind | undefined => {
  const approved = selectApprovedRequests(requests)
  if (approved.length === 0) return undefined
  return approved.some(
    (request) =>
      request.approvalState === BiddingApprovalState.APPROVED ||
      deriveJepxReach(request.sendStatus) !== 'reached',
  )
    ? 'unsent'
    : 'sent'
}

/**
 * 承認済の主ボタンの押せない理由の文末を返す。 design `spot.html` の `__spotSubmitReason` の verb と
 * 同じく、 承認モードの受渡日は「承認申請」、 それ以外は「提出」 (ボタンの動詞「N件の入札を承認申請」 /
 * 「N件の入札を提出」 とそろえる)。
 *
 * 札に保存した承認モードでなく、 承認済の依頼の札のエリアの承認フロー設定の現在値 (`currentModes`) で
 * 決める (design の `submitMode`)。 承認済の札は要承認で承認を経たが、 承認の後に設定が自動・承認なしへ
 * 変わった受渡日は「提出」 になる。 エリアで混ざる時は要承認側 ([[isDirectSubmitMode]])。
 * 設定を読めていない間 (undefined) と対象のエリアが無い時は、 承認を経た札の既定の「承認申請」。
 */
export const resolveApprovedActionVerb = (
  currentModes: readonly BiddingApprovalMode[] | undefined,
): ApprovedActionVerb =>
  currentModes !== undefined && isDirectSubmitMode(currentModes)
    ? 'submit'
    : 'request'
